import * as THREE from 'three'
import type { SceneManager } from './SceneManager'
import { getThreeObject } from './ObjectManager'

export type OnDragEndCallback = (id: string, position: [number, number, number]) => void
export type OnDragMoveCallback = (id: string, x: number, z: number) => void
/** Ctrl/Meta 拖拽时返回贴附高度（对象顶面或楼面）；不按修饰键则不要调用 */
export type GetSurfaceSnapYFn = (e: MouseEvent, excludeIds: string[]) => number | null
export type GetSnapPositionFn = (x: number, z: number, excludeId: string) => { x: number; z: number } | null
export type MultiDragUpdate = { id: string; position: [number, number, number] }
export type OnMultiDragEndCallback = (updates: MultiDragUpdate[]) => void
export type GetMultiDragContextFn = (e: MouseEvent, dragObjectId: string) => {
  ids: string[]
  getPosition: (id: string) => [number, number, number] | undefined
} | null

// 吸附到网格
function snapToGrid(val: number, gridSize = 1): number {
  return Math.round(val / gridSize) * gridSize
}

export class DragHandler {
  private _sm: SceneManager
  private _onDragEnd: OnDragEndCallback
  private _getHandleCheck: (() => ((e: PointerEvent) => boolean) | undefined) | undefined
  private _isDragging = false
  private _dragObjectId: string | null = null
  private _raycaster = new THREE.Raycaster()
  private _groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)
  private _dragStartMousePos = { x: 0, y: 0 }
  private _dragOffset = { x: 0, z: 0 }
  private _dragPlaneY = 0
  private _snapSize = 1

  // 对齐辅助线
  private _snapLines: THREE.Line[] = []

  private _onDragMove?: OnDragMoveCallback
  private _getSnapPosition?: GetSnapPositionFn
  private _getSurfaceSnapY?: GetSurfaceSnapYFn
  private _getMultiDragContext?: GetMultiDragContextFn
  private _onMultiDragEnd?: OnMultiDragEndCallback
  private _shouldBlockDrag?: () => boolean
  private _isObjectLocked?: (id: string) => boolean
  private _multiDragIds: string[] = []
  private _multiDragStartPositions = new Map<string, [number, number, number]>()

  constructor(
    sm: SceneManager,
    onDragEnd: OnDragEndCallback,
    getHandleCheck?: () => ((e: PointerEvent) => boolean) | undefined,
    opts?: {
      onDragMove?: OnDragMoveCallback
      getSnapPosition?: GetSnapPositionFn
      /** Ctrl/Meta 拖拽：吸附到指针下对象顶面或楼面 */
      getSurfaceSnapY?: GetSurfaceSnapYFn
      getMultiDragContext?: GetMultiDragContextFn
      onMultiDragEnd?: OnMultiDragEndCallback
      shouldBlockDrag?: () => boolean
      /** 对象级 locked：为 true 时禁止拖动（含多选整体拖） */
      isObjectLocked?: (id: string) => boolean
    }
  ) {
    this._sm = sm
    this._onDragEnd = onDragEnd
    this._getHandleCheck = getHandleCheck
    this._onDragMove = opts?.onDragMove
    this._getSnapPosition = opts?.getSnapPosition
    this._getSurfaceSnapY = opts?.getSurfaceSnapY
    this._getMultiDragContext = opts?.getMultiDragContext
    this._onMultiDragEnd = opts?.onMultiDragEnd
    this._shouldBlockDrag = opts?.shouldBlockDrag
    this._isObjectLocked = opts?.isObjectLocked
    this._bind()
  }

  private _bind() {
    const canvas = this._sm.renderer.domElement
    canvas.addEventListener('mousedown', this._onMouseDown)
    window.addEventListener('mousemove', this._onMouseMove)
    window.addEventListener('mouseup', this._onMouseUp)
  }

  setDragTarget(id: string) {
    this._dragObjectId = id
  }

  clearDragTarget() {
    this._dragObjectId = null
  }

  private _onMouseDown = (e: MouseEvent) => {
    if (e.button !== 0) return
    if (this._shouldBlockDrag?.()) return

    // 如果点中了控制柄，不处理对象拖动
    const handleCheck = this._getHandleCheck?.()
    if (handleCheck?.(e as unknown as PointerEvent)) return

    this._dragStartMousePos = { x: e.clientX, y: e.clientY }
    this._dragObjectId = null

    // 自动检测鼠标按下时命中的 3D 对象
    const canvas = this._sm.renderer.domElement
    const rect = canvas.getBoundingClientRect()
    // 只有点在 canvas 内才处理
    if (
      e.clientX < rect.left || e.clientX > rect.right ||
      e.clientY < rect.top  || e.clientY > rect.bottom
    ) return

    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1
    )
    this._raycaster.setFromCamera(ndc, this._sm.camera)
    const hits = this._raycaster.intersectObjects(this._sm.scene.children, true)
    for (const hit of hits) {
      if (hit.object.parent?.name === '__ground__' || hit.object.name === '__ground__') continue
      // 与 SelectHandler 保持一致：拆壳隐藏的结构件 / 非当前层对象不参与拖拽拾取
      if (hit.object.userData.interactable === false) continue
      const id = hit.object.userData.objectId as string | undefined
      if (id) {
        this._dragObjectId = id
        break
      }
    }

    if (this._dragObjectId && this._isObjectLocked?.(this._dragObjectId)) {
      this._dragObjectId = null
      return
    }

    if (this._dragObjectId) {
      const g = getThreeObject(this._dragObjectId)
      this._dragPlaneY = g?.position.y ?? 0
      this._groundPlane.set(new THREE.Vector3(0, 1, 0), -this._dragPlaneY)
      const gp = this._getGroundPos(e, this._dragObjectId)
      if (g && gp) {
        this._dragOffset.x = gp.x - g.position.x
        this._dragOffset.z = gp.z - g.position.z
      } else {
        this._dragOffset.x = 0
        this._dragOffset.z = 0
      }
    }

    this._multiDragIds = []
    this._multiDragStartPositions.clear()
    if (this._dragObjectId && this._getMultiDragContext) {
      const ctx = this._getMultiDragContext(e, this._dragObjectId)
      if (ctx && ctx.ids.length > 1 && ctx.ids.includes(this._dragObjectId)) {
        const lockedFn = this._isObjectLocked
        if (lockedFn && ctx.ids.some((id) => lockedFn(id))) {
          this._dragObjectId = null
          return
        }
        this._multiDragIds = [...ctx.ids]
        for (const id of this._multiDragIds) {
          const p = ctx.getPosition(id)
          if (p) this._multiDragStartPositions.set(id, p)
        }
      }
    }
  }

  private _onMouseMove = (e: MouseEvent) => {
    if (!this._dragObjectId) return
    if (e.buttons !== 1) return

    const dx = e.clientX - this._dragStartMousePos.x
    const dy = e.clientY - this._dragStartMousePos.y
    const dist = Math.sqrt(dx * dx + dy * dy)

    if (!this._isDragging && dist > 5) {
      this._isDragging = true
      // 禁用 OrbitControls 防止同时旋转视角
      this._sm.controls.enabled = false
    }

    if (!this._isDragging) return

    const pos = this._getGroundPos(e, this._dragObjectId)
    if (!pos) return

    const group = getThreeObject(this._dragObjectId)
    if (!group) return

    const targetX = pos.x - this._dragOffset.x
    const targetZ = pos.z - this._dragOffset.z
    const excludeIds = this._multiDragIds.length > 0 ? this._multiDragIds : [this._dragObjectId]
    const wantSnap = e.ctrlKey || e.metaKey
    const snapY = wantSnap ? this._getSurfaceSnapY?.(e, excludeIds) : null

    if (this._multiDragIds.length > 0) {
      const startLead = this._multiDragStartPositions.get(this._dragObjectId)
      if (startLead) {
        const deltaX = targetX - startLead[0]
        const deltaZ = targetZ - startLead[2]
        const deltaY = snapY != null ? snapY - startLead[1] : 0
        for (const id of this._multiDragIds) {
          const start = this._multiDragStartPositions.get(id)
          const obj3d = getThreeObject(id)
          if (start && obj3d) {
            obj3d.position.x = start[0] + deltaX
            obj3d.position.z = start[2] + deltaZ
            if (snapY != null) obj3d.position.y = start[1] + deltaY
          }
        }
      }
    } else {
      group.position.x = targetX
      group.position.z = targetZ
      if (snapY != null) group.position.y = snapY
    }
    this._onDragMove?.(this._dragObjectId, targetX, targetZ)
    this._sm.markDirty()
  }

  private _onMouseUp = (e: MouseEvent) => {
    const wasDragging = this._isDragging
    this._isDragging = false
    this._sm.controls.enabled = true
    this._clearSnapLines()

    if (!this._dragObjectId || !wasDragging) {
      this._dragObjectId = null
      this._multiDragIds = []
      this._multiDragStartPositions.clear()
      return
    }

    const pos = this._getGroundPos(e, this._dragObjectId)
    if (pos) {
      const targetX = pos.x - this._dragOffset.x
      const targetZ = pos.z - this._dragOffset.z
      if (this._multiDragIds.length > 0 && this._onMultiDragEnd) {
        const startLead = this._multiDragStartPositions.get(this._dragObjectId)
        if (startLead) {
          const deltaX = targetX - startLead[0]
          const deltaZ = targetZ - startLead[2]
          const updates: MultiDragUpdate[] = this._multiDragIds
            .map(id => {
              const start = this._multiDragStartPositions.get(id)
              if (!start) return null
              const g = getThreeObject(id)
              const y = g?.position.y ?? start[1]
              return {
                id,
                position: [start[0] + deltaX, y, start[2] + deltaZ] as [number, number, number]
              }
            })
            .filter((u): u is MultiDragUpdate => u !== null)
          if (updates.length) this._onMultiDragEnd(updates)
        }
      } else {
        const dragObj = getThreeObject(this._dragObjectId)
        const y = dragObj?.position.y ?? 0
        this._onDragEnd(this._dragObjectId, [targetX, y, targetZ])
      }
    }
    this._dragObjectId = null
    this._dragOffset.x = 0
    this._dragOffset.z = 0
    this._multiDragIds = []
    this._multiDragStartPositions.clear()
  }

  private _getGroundPos(e: MouseEvent, excludeId?: string | null): { x: number; z: number } | null {
    const canvas = this._sm.renderer.domElement
    const rect = canvas.getBoundingClientRect()

    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1
    )

    this._raycaster.setFromCamera(ndc, this._sm.camera)
    const target = new THREE.Vector3()
    const hit = this._raycaster.ray.intersectPlane(this._groundPlane, target)
    if (!hit) return null

    const rawX = snapToGrid(target.x, this._snapSize)
    const rawZ = snapToGrid(target.z, this._snapSize)

    if (this._getSnapPosition && excludeId) {
      const snapped = this._getSnapPosition(rawX, rawZ, excludeId)
      if (snapped) return snapped
    }

    return { x: rawX, z: rawZ }
  }

  private _clearSnapLines() {
    for (const line of this._snapLines) {
      this._sm.scene.remove(line)
      line.geometry.dispose()
    }
    this._snapLines = []
  }

  dispose() {
    const canvas = this._sm.renderer.domElement
    canvas.removeEventListener('mousedown', this._onMouseDown)
    window.removeEventListener('mousemove', this._onMouseMove)
    window.removeEventListener('mouseup', this._onMouseUp)
  }
}

export interface ClickGroundOptions {
  /** 网格边长（米），默认 1 */
  gridSize?: number
  /** 地面高度（米），默认 0 */
  groundY?: number
}

// 从鼠标/指针事件获取地面交点（用于 Alt+点击复制、路径/布局笔画等）
export function getClickWorldPosition(
  event: { clientX: number; clientY: number },
  canvas: HTMLCanvasElement,
  camera: THREE.Camera,
  options?: ClickGroundOptions
): { x: number; y: number; z: number } | null {
  const gridSize = options?.gridSize ?? 1
  const groundY = options?.groundY ?? 0
  const rect = canvas.getBoundingClientRect()
  const ndc = new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1
  )
  const raycaster = new THREE.Raycaster()
  raycaster.setFromCamera(ndc, camera)
  const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -groundY)
  const point = new THREE.Vector3()
  const hit = raycaster.ray.intersectPlane(groundPlane, point)
  if (!hit) return null
  const gx = Math.round(point.x / gridSize) * gridSize
  const gz = Math.round(point.z / gridSize) * gridSize
  return {
    x: gx,
    y: groundY,
    z: gz
  }
}

// HTML5 拖拽放置（从组件托盘拖到场景）
export function getDropWorldPosition(
  event: DragEvent,
  canvas: HTMLCanvasElement,
  camera: THREE.Camera,
  options?: ClickGroundOptions
): THREE.Vector3 | null {
  const pos = getClickWorldPosition(event, canvas, camera, options)
  if (!pos) return null
  return new THREE.Vector3(pos.x, pos.y, pos.z)
}
